import { Canvas } from 'fabric/fabric-impl';
import { useEffect, useRef, useState } from 'react';
import { Button, Popover, Tooltip } from 'antd';
import { BrowserView, isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';

import SharingSocialMedia from '../SharingSocialMedia/SharingSocialMedia';
import { EditorStore, useStore } from '../../store/createStore';
import { CanvasHandler } from '../../handlers/CanvasHandler';
import Header from '../UI/Header';
import HeaderButton from '../UI/HeaderButton';

import styles from './HeaderEditorBase.module.less';

import header from '../../assets/images/HOME_rejilla.svg';
import undo from '../../assets/images/deshacer.svg';
import redo from '../../assets/images/rehacer.svg';
import arrange from '../../assets/images/organizar.svg';
import download from '../../assets/images/descargar.svg';
import close from '../../assets/images/cerrar.svg';
import zoomIn from '../../assets/images/ZOOM_IN.svg';
import zoomOut from '../../assets/images/ZOOM_OUT.svg';
import pan from '../../assets/images/PAN.svg';
import panSelected from '../../assets/images/PAN_SELECTED.svg';
import pointer from '../../assets/images/POINTER.svg';
import pointerSelected from '../../assets/images/POINTER_SELECTED.svg';
import { ReactComponent as SettleFront } from '../../assets/images/organizar.svg';
import { ReactComponent as SettleBack } from '../../assets/images/icons/SETTLE_BACK.svg';

function HeaderEditorBase() {
  const [showTooltip, setShowTooltip] = useState({
    home: false,
    undo: false,
    redo: false,
    arrange: false,
    zoomOut: false,
    zoomIn: false,
    cursor: false,
    pan: false,
    download: false,
  });
  const [imageBase64, setImageBase64] = useState<string>('');
  const [isVisibleShare, setVisibleShare] = useState(false);
  const [settleLayer, setSettleLayerRef] = useState(null);
  const [downloadRef, setDownloadRef] = useState(null);
  const [isArrangeVisible, setArrangeVisible] = useState(false);
  const [dragMode, setDragMode] = useStore<EditorStore, boolean>('dragMode');
  const setTourStepRef = useStore<EditorStore, any>('tourStepsRef')[1];
  const [canvas] = useStore<EditorStore, Canvas>('canvas');
  const [zoom, setZoom] = useState(100);
  const canvasHandler = new CanvasHandler(canvas);

  useEffect(() => {
    if (!settleLayer) {
      return;
    }
    handleTourStepRef(settleLayer, 1);
  }, [settleLayer]);

  useEffect(() => {
    if (!downloadRef) {
      return;
    }
    handleTourStepRef(downloadRef, 2);
  }, [downloadRef]);

  const handleDragMode = () => {
    setDragMode(!dragMode);
    // @ts-ignore
    canvasHandler.toggleDragMode(!dragMode);
  };

  const handleZoomOut = () => {
    if (zoom > 75) {
      canvasHandler.zoomOut();
      setZoom((state) => state - 5);
    }
  };
  const handleZoomIn = () => {
    if (zoom < 150) {
      canvasHandler.zoomIn();
      setZoom((state) => state + 5);
    }
  };

  const handleDownload = () => {
    canvasHandler.downloadCropImageFromCanvas().then((image) => {
      setImageBase64(image);
      setVisibleShare(true);
    });
  };

  const handleSendBackwards = () => {
    canvasHandler.sendBackwards();
  };

  const handleBringForward = () => {
    canvasHandler.bringForward();
  };

  const undoChange = () => {
    // @ts-ignore
    canvas.undo();
  };

  const redoChange = () => {
    // @ts-ignore
    canvas.redo();
  };

  const handleShowTooltip = (tooltipName: string, newValue: boolean) => {
    return () => {
      setShowTooltip((state) => ({ ...state, [tooltipName]: newValue }));
    };
  };

  const handleTourStepRef = (value: HTMLElement, index: number) => {
    setTourStepRef((state: any[]) => {
      const oldStep = state[index];
      const newStep = { ...oldStep, ref: value };
      const filterState = state.filter((item) => item.index !== index);
      return [...filterState, newStep].sort((a, b) => a.index - b.index);
    });
  };

  return (
    <>
      <SharingSocialMedia
        imageBase64={imageBase64}
        isVisible={isVisibleShare}
        onSetVisible={setVisibleShare}
      />
      <Header className="justify-between">
        <div className="flex">
          <Tooltip placement="bottomLeft" title="Menú" visible={showTooltip.home && !isMobile}>
            <Link to="/home">
              <HeaderButton
                onMouseOver={handleShowTooltip('home', true)}
                onMouseLeave={handleShowTooltip('home', false)}
                iconFile={header}
              />
            </Link>
          </Tooltip>
        </div>

        <div className="flex items-center">
          <Tooltip placement="bottom" title="Deshacer" visible={showTooltip.undo && !isMobile}>
            <HeaderButton
              onMouseOver={handleShowTooltip('undo', true)}
              onMouseLeave={handleShowTooltip('undo', false)}
              onClick={undoChange}
              className="ml-6 cursor-pointer"
              iconFile={undo}
            />
          </Tooltip>
          <Tooltip placement="bottom" title="Rehacer" visible={showTooltip.redo && !isMobile}>
            <HeaderButton
              onMouseOver={handleShowTooltip('redo', true)}
              onMouseLeave={handleShowTooltip('redo', false)}
              onClick={redoChange}
              className="ml-6 cursor-pointer"
              iconFile={redo}
            />
          </Tooltip>

          <Popover
            content={
              <>
                <Button
                  type="primary"
                  onClick={handleBringForward}
                  className="flex text-secondary w-full"
                  icon={<SettleFront width={20} height={20} className="mr-2" />}
                >
                  Traer al frente
                </Button>
                <Button
                  type="primary"
                  onClick={handleSendBackwards}
                  className="flex text-secondary mt-2 w-full"
                  icon={<SettleBack width={20} height={20} className="mr-2" />}
                >
                  Enviar detrás
                </Button>
              </>
            }
            trigger="click"
            visible={isArrangeVisible}
            onVisibleChange={(visible) => setArrangeVisible(visible)}
          >
            <Tooltip placement="bottom" title="Ordenar" visible={showTooltip.arrange && !isMobile}>
              <HeaderButton
                onMouseOver={handleShowTooltip('arrange', true)}
                onMouseLeave={handleShowTooltip('arrange', false)}
                ref={setSettleLayerRef}
                className="ml-6"
                iconFile={arrange}
              />
            </Tooltip>
          </Popover>
          <BrowserView renderWithFragment={true}>
            <Tooltip placement="bottom" title="Reducir" visible={showTooltip.zoomOut && !isMobile}>
              <HeaderButton
                onMouseOver={handleShowTooltip('zoomOut', true)}
                onMouseLeave={handleShowTooltip('zoomOut', false)}
                className="ml-6 mr-2.5"
                onClick={handleZoomOut}
                iconFile={zoomOut}
              />
            </Tooltip>
            <div
              className={`text-center bg-secondary text-primary ml-1 ${styles['header-editor-base__zoom-info']}`}
            >
              <span className="align-middle">{zoom}%</span>
            </div>
            <Tooltip placement="bottom" title="Ampliar" visible={showTooltip.zoomIn && !isMobile}>
              <HeaderButton
                onMouseOver={handleShowTooltip('zoomIn', true)}
                onMouseLeave={handleShowTooltip('zoomIn', false)}
                onClick={handleZoomIn}
                className="ml-2.5"
                iconFile={zoomIn}
              />
            </Tooltip>
            <Tooltip placement="bottom" title="Selección" visible={showTooltip.cursor && !isMobile}>
              <HeaderButton
                onMouseOver={handleShowTooltip('cursor', true)}
                onMouseLeave={handleShowTooltip('cursor', false)}
                onClick={dragMode ? handleDragMode : void 0}
                className="ml-6"
                iconFile={dragMode ? pointer : pointerSelected}
              />
            </Tooltip>
            <Tooltip placement="bottom" title="Mover" visible={showTooltip.pan && !isMobile}>
              <HeaderButton
                onMouseOver={handleShowTooltip('pan', true)}
                onMouseLeave={handleShowTooltip('pan', false)}
                onClick={!dragMode ? handleDragMode : void 0}
                className="ml-1"
                iconFile={dragMode ? panSelected : pan}
              />
            </Tooltip>
          </BrowserView>
        </div>

        <div className="flex">
          <Tooltip
            placement="bottomRight"
            title="Descargar"
            visible={showTooltip.download && !isMobile}
          >
            <HeaderButton
              onMouseOver={handleShowTooltip('download', true)}
              onMouseLeave={handleShowTooltip('download', false)}
              ref={setDownloadRef}
              onClick={handleDownload}
              className="ml-8"
              iconFile={download}
            />
          </Tooltip>
        </div>
      </Header>
    </>
  );
}

export default HeaderEditorBase;
